import { IColumn } from "@fluentui/react";
import React, { ReactElement, useEffect, useState } from "react";

import { DateTimeDisplay } from "../../hooks";
import { ensure } from "../../utils/ensure/ensure";
import { getFileExtension, getFileName } from "../../utils/file/filePath";
import { ButtonRenderProps } from "../inputs/button/ButtonRenderProps";
import { CommandBarButton } from "../inputs/button/CommandBarButton";
import { SearchBox } from "../inputs/search-box/SearchBox";
import { ContentSection } from "../items/ContentSection";
import { Icon } from "../items/Icon";
import { Text } from "../items/Text";
import { Stack } from "../layouts/stack/Stack";
import { DetailsListTable } from "../lists/DetailsListTable";
import { DetailsRowProps, RenderFunction } from "../lists/ListTable";
import { createListTableColumns, ListTableColumn } from "../lists/ListTableColumn";
import { ListTableSort } from "../lists/ListTableSort";
import { Breadcrumb, BreadcrumbItem } from "../navigations/Breadcrumb";
import { Link } from "../navigations/Link";
import { Theme } from "../theme/Theme";
import { mapItemToBucketObject, BucketFile, BucketObject } from "./BucketObject";
import { BucketObjectItem } from "./BucketObjectItem";
import { GetBucketFilesQuery } from "./queries/GetBucketFilesQuery";
import { SearchBucketQuery } from "./queries/SearchBucketQuery";
import { BucketObjectType } from "./types/BucketObjectType";
import { useBucketExplorerFilters } from "./useBucketExplorerFilters";

interface BucketContentExplorerProps {
    bucketUniqueId: string;
    selection?: {
        selectMany?: {
            text: string;
            icon: string;
            onClick(selectedItems: BucketObject[]): void;
        };
        onChange?(selectedItems: BucketObject[]): void;
    };
    onFileClick?(file: BucketFile): void;
    onFolderClick?(query: GetBucketFilesQuery): Promise<BucketObject[]>;
    onSearch(query: SearchBucketQuery): Promise<BucketObject[]>;
    bucketName?: string;
}

export const BucketContentExplorer: React.VFC<BucketContentExplorerProps> = ({
    bucketUniqueId,
    selection,
    onFileClick,
    onFolderClick,
    onSearch,
    bucketName
}) => {
    const {
        fileName,
        pathFolders,
        sort,
        openFolder,
        goBackToFolder,
        root,
        setFileName,
        setSort,
        sortBucketContent
    } = useBucketExplorerFilters();

    const [isContentLoading, setIsContentLoading] = useState<boolean>(false);
    const [currentObjects, setCurrentObjects] = useState<BucketObjectItem[]>([]);
    const [selectedObjects, setSelectedObjects] = useState<BucketObjectItem[]>([]);
    const [cache, setCache] = useState<Record<string, BucketObjectItem[]>>({});

    const currentPath = pathFolders.length ? pathFolders.join("/") : "/";

    useEffect(() => {
        if (fileName && fileName.length >= 3) {
            root();

            handleSearch(fileName);
        } else {
            loadData(currentPath, sort);
        }
    }, [fileName]);

    useEffect(() => {
        loadData(currentPath, sort);
    }, [currentPath]);

    useEffect(() => {
        const sortedContent = sortBucketContent(currentObjects, sort);
        setCurrentObjects(sortedContent);
    }, [sort]);

    const handleSearch = async (name: string) => {
        setIsContentLoading(true);

        onSearch({ name, sortBy: sort.by, sortOrder: sort.order, bucketName })
            .then((items) => {
                const bucketSearchObjectItems = items.map((item) => mapItemToBucketObject(item));

                setCurrentObjects(bucketSearchObjectItems);
            })
            .finally(() => setIsContentLoading(false));
    };

    const loadData = async (path: string, currentSort: ListTableSort) => {
        const key = createKeyFromRequest({ parentPath: path, sortBy: currentSort.by, sortOrder: currentSort.order });

        const cached = cache[key];

        if (cached) {
            setCurrentObjects(cached);
            return;
        }

        ensure(onFolderClick);

        setIsContentLoading(true);

        onFolderClick({
            sortBy: currentSort.by,
            sortOrder: currentSort.order,
            parentPath: path,
            bucketName: bucketName || ""
        })
            .then((items) => {
                const bucketItems = items.map((bucketItem) => mapItemToBucketObject(bucketItem));

                setCurrentObjects(bucketItems);

                /* bucketItems might be empty if the request was aborted; this condition prevents displaying
                    empty list if the request was re-triggered with a subsequent render e.g. fileName
                    changed to an empty value, which should show bucket's root */
                if (bucketItems.length) {
                    setCache((oldCache) => ({
                        ...oldCache,
                        [key]: bucketItems
                    }));
                }
            })
            .finally(() => {
                setIsContentLoading(false);
            });
    };

    const handleItemClicked = (item: BucketObjectItem) => {
        if (item.type === BucketObjectType.Folder) {
            openFolder(item.name);
        } else if (onFileClick) {
            onFileClick(item);
        }
    };

    const handleSelection = selection
        ? (selectedItems: BucketObjectItem[]) => {
              setSelectedObjects(selectedItems);

              if (selection.onChange) {
                  selection.onChange(selectedItems.map(mapItemToBucketObject));
              }
          }
        : undefined;

    const handleGoBackToFolder = (folderName: string) => {
        goBackToFolder(folderName);
    };

    const handleRootClick = (): void => {
        root();
    };

    const createKeyFromRequest = (request: GetBucketFilesQuery) =>
        `${request.parentPath}_${request.sortBy}_${request.sortOrder}`;

    const columns = createListTableColumns<BucketObjectItem>([
        {
            type: "interactive",
            key: "name",
            name: "Name",
            minWidth: 250,
            isSortable: true,
            onItemInvoked: handleItemClicked,
            onRender: renderObjectName
        },
        {
            type: "basic",
            key: "sizeInBytes",
            name: "File size",
            isResizable: false,
            minWidth: 100,
            onRender: renderFileSize
        },
        {
            type: "basic",
            key: "type",
            name: "Type",
            isResizable: false,
            isSortable: true,
            minWidth: 100,
            onRender: renderType
        },
        {
            type: "basic",
            key: "lastModified",
            name: "Last modified",
            minWidth: 160,
            isResizable: false,
            isSortable: true,
            onRender: renderDateTime
        }
    ]);

    const explorerBreadcrumbs = (
        <Breadcrumb
            items={[
                { key: bucketUniqueId, text: bucketUniqueId, onClick: handleRootClick },
                ...pathFolders.map<BreadcrumbItem>((folderName) => ({
                    key: folderName,
                    text: folderName,
                    onClick: () => handleGoBackToFolder(folderName)
                }))
            ]}
            testId="explorer-breadcrumbs"
        />
    );

    const buttons = selection?.selectMany
        ? [
              {
                  text: selection.selectMany.text,
                  icon: selection.selectMany.icon,
                  onClick: () => selection.selectMany?.onClick(selectedObjects.map(mapItemToBucketObject)),
                  disabled: selectedObjects.length === 0,
                  render: SelectedManyActionButton,
                  testId: "explorer-actions-upload"
              } as ButtonRenderProps<SelectedManyActionButtonProps>
          ]
        : undefined;

    const isItemSelectable = (item: BucketObjectItem): boolean => {
        if (item.isSelectable === undefined) {
            return true;
        }

        return typeof item.isSelectable === "string" ? item.isSelectable.length === 0 : item.isSelectable;
    };
    return (
        <ContentSection header={explorerBreadcrumbs} buttons={buttons}>
            <Stack tokens={{ maxWidth: 300 }} styles={{ root: { paddingLeft: 50 } }}>
                <SearchBox
                    placeholder="File name"
                    onChange={setFileName}
                    value={fileName}
                    testId={"explorer-filters-name"}
                />
            </Stack>
            <Stack>
                <DetailsListTable
                    columns={columns}
                    items={currentObjects}
                    isLoading={isContentLoading}
                    noItemsMessage="This folder is empty"
                    sort={sort}
                    onSortChange={setSort}
                    onRenderRow={renderExplorerRow}
                    isItemSelectable={isItemSelectable}
                    onSelectionChange={handleSelection}
                />
            </Stack>
        </ContentSection>
    );
};

interface SelectedManyActionButtonProps {
    text: string;
    icon: string;
    disabled?: boolean;
    onClick(): void;
    testId?: string;
}

const SelectedManyActionButton: React.VFC<SelectedManyActionButtonProps> = ({
    text,
    icon,
    disabled,
    onClick,
    testId
}: SelectedManyActionButtonProps) => (
    <CommandBarButton
        text={text}
        icon={icon}
        onClick={onClick}
        testId={testId}
        styles={{ root: { width: text.length * 10 } }}
        disabled={disabled}
    />
);

const renderExplorerRow = (props?: DetailsRowProps, defaultRender?: RenderFunction<DetailsRowProps>) => {
    ensure(props);
    ensure(defaultRender);

    const testProps = {
        "data-testid": "explorer-row",
        "data-testkey": props.item.id,
        "data-testdisabled": props.item.isInactive,
        "data-testname": getFileName(props.item.name),
        "data-testext": props.item.type === BucketObjectType.File ? getFileExtension(props.item.name) : ""
    };

    const item = props.item as BucketObjectItem;

    const colors = getOverriddenCellColors(isActiveObject(item));

    return defaultRender({
        ...props,
        ...testProps,
        id: item.id ? `explorer-item-${item.id}` : undefined,
        styles: {
            ...props.styles,
            root: {
                ...(props.styles ? props.styles["root"] : undefined),
                ...colors
            }
        }
    });
};

const renderObjectName = (
    item?: BucketObjectItem,
    _index?: number,
    column?: ListTableColumn<unknown> & IColumn
): ReactElement | null => {
    ensure(item);
    ensure(column);
    ensure(column.currentWidth);

    const maxCellWidth = column.currentWidth - 30;
    const iconSize = 18;

    const isActive = isActiveObject(item);

    const onClick = () => column.onItemInvoked && column.onItemInvoked(item);

    return (
        <Stack horizontal={true} verticalAlign="center" tokens={{ childrenGap: 15, maxWidth: maxCellWidth }}>
            <Icon size={iconSize} iconName={item.type === BucketObjectType.Folder ? "FabricFolder" : "Page"} />
            <Link
                disabled={!isActive}
                tooltipContent={getInactiveFileTooltip(item)}
                onClick={onClick}
                styles={{
                    root: { width: "100%" }
                }}
            >
                <Text
                    value={item.name}
                    styles={{ root: { ...getOverriddenCellColors(isActive) } }}
                    maxLength={maxCellWidth / 10}
                    tooltip={getInactiveFileTooltip(item)}
                    variant="smallPlus"
                />
            </Link>
        </Stack>
    );
};

const renderFileSize = (item?: BucketObjectItem): ReactElement | null => {
    ensure(item);

    if (item.type === BucketObjectType.File) {
        return <Stack>{formatBytes(item.sizeInBytes)}</Stack>;
    }

    return <Stack>-</Stack>;
};

const formatBytes = (bytes: number, decimals = 2) => {
    if (bytes === 0) {
        return "0 Bytes";
    }

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

const renderType = (item?: BucketObjectItem): ReactElement | null => {
    ensure(item);

    if (item.type === BucketObjectType.Folder) {
        return <Stack>Folder</Stack>;
    }

    return <Stack>{getFileExtension(item.path)}</Stack>;
};

const renderDateTime = (item?: BucketObjectItem): ReactElement | null => {
    ensure(item);

    if (item.type === BucketObjectType.File) {
        return (
            <Stack>
                <DateTimeDisplay date={item.lastModified} />
            </Stack>
        );
    }

    return <Stack>-</Stack>;
};

const getOverriddenCellColors = (isActive: boolean) =>
    isActive
        ? undefined
        : {
              backgroundColor: Theme.semanticColors.disabledBackground,
              color: Theme.semanticColors.disabledText
          };

const isActiveObject = (item: BucketObjectItem): boolean => {
    if (item.type === BucketObjectType.File && item.isInactive === true) {
        return false;
    }

    return true;
};

const getInactiveFileTooltip = (item: BucketObjectItem): string | undefined => {
    if (item.type === BucketObjectType.File && !isActiveObject(item) && typeof item.isSelectable === "string") {
        return item.isSelectable;
    }

    return undefined;
};
